import { useState, useRef, useEffect } from 'react'
import { createPortal } from 'react-dom'
import { NavLink, useLocation } from 'react-router-dom'
import { MessageSquare, Plug, Settings, LogOut, ChevronDown, Plus, Trash2, MoreHorizontal, Pencil, Archive, ArchiveRestore, Bot } from 'lucide-react'
import { useAuth } from '../../auth/AuthProvider'
import { useConversations } from '../../contexts/ConversationContext'
import { usePushNotifications } from '../../hooks/usePushNotifications'
import { SeguruSymbol } from '../SeguruSymbol'
import type { ReactNode } from 'react'

// Nav surfaces dropped in the "chat is the UI" sweep:
// - /briefing (users scroll the conversation list; briefings are conversations)
// - /conhecimento (users ask "o que sabes de mim?" in chat; get_identity_summary tool)
// Functionality survives — just not as dedicated tabs.

const mainNavItems = [
  { to: '/chat', icon: MessageSquare, label: 'Chat' },
  { to: '/rotinas', icon: Bot, label: 'Rotinas' },
]

const mobileNavItems = [
  { to: '/chat', icon: MessageSquare, label: 'Chat' },
  { to: '/rotinas', icon: Bot, label: 'Rotinas' },
  { to: '/connectors', icon: Plug, label: 'Conectores' },
]

function initials(name: string | null | undefined, email: string | null | undefined): string {
  if (name && name.trim()) {
    const parts = name.trim().split(/\s+/)
    if (parts.length >= 2) return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase()
    return parts[0].slice(0, 2).toUpperCase()
  }
  if (email) return email.slice(0, 2).toUpperCase()
  return '?'
}

function ConvMenu({ convId, onRename, isArchived = false }: { convId: string; onRename: () => void; isArchived?: boolean }) {
  const { deleteConversation, updateConversation } = useConversations()
  const [open, setOpen] = useState(false)
  const [menuPos, setMenuPos] = useState({ top: 0, left: 0 })
  const btnRef = useRef<HTMLButtonElement>(null)

  function openMenu(e: React.MouseEvent) {
    e.stopPropagation()
    if (!btnRef.current) return
    const rect = btnRef.current.getBoundingClientRect()
    setMenuPos({ top: rect.bottom + 4, left: rect.left })
    setOpen(o => !o)
  }

  useEffect(() => {
    if (!open) return
    function handleClick() { setOpen(false) }
    document.addEventListener('mousedown', handleClick)
    return () => document.removeEventListener('mousedown', handleClick)
  }, [open])

  const menu = open ? createPortal(
    <div
      onMouseDown={e => e.stopPropagation()}
      className="fixed w-40 rounded-lg border overflow-hidden shadow-lg z-[9999]"
      style={{ top: menuPos.top, left: menuPos.left, background: 'var(--cream)', borderColor: 'var(--border)' }}
    >
      <button
        onClick={e => { e.stopPropagation(); setOpen(false); onRename() }}
        className="w-full flex items-center gap-2.5 px-3 py-2 text-xs transition-colors"
        style={{ color: 'var(--ink-light)' }}
        onMouseEnter={e => e.currentTarget.style.background = 'var(--cream-dark)'}
        onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
      >
        <Pencil size={12} style={{ color: 'var(--ink-muted)' }} />
        Renomear
      </button>
      {isArchived ? (
        <button
          onClick={async e => { e.stopPropagation(); setOpen(false); await updateConversation(convId, { is_archived: false }) }}
          className="w-full flex items-center gap-2.5 px-3 py-2 text-xs transition-colors"
          style={{ color: 'var(--ink-light)' }}
          onMouseEnter={e => e.currentTarget.style.background = 'var(--cream-dark)'}
          onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
        >
          <ArchiveRestore size={12} style={{ color: 'var(--ink-muted)' }} />
          Desarquivar
        </button>
      ) : (
        <button
          onClick={async e => { e.stopPropagation(); setOpen(false); await updateConversation(convId, { is_archived: true }) }}
          className="w-full flex items-center gap-2.5 px-3 py-2 text-xs transition-colors"
          style={{ color: 'var(--ink-light)' }}
          onMouseEnter={e => e.currentTarget.style.background = 'var(--cream-dark)'}
          onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
        >
          <Archive size={12} style={{ color: 'var(--ink-muted)' }} />
          Arquivar
        </button>
      )}
      <div style={{ borderTop: '1px solid var(--border)' }} />
      <button
        onClick={async e => { e.stopPropagation(); setOpen(false); await deleteConversation(convId) }}
        className="w-full flex items-center gap-2.5 px-3 py-2 text-xs text-red-600 hover:bg-red-50 transition-colors"
      >
        <Trash2 size={12} />
        Apagar
      </button>
    </div>,
    document.body
  ) : null

  return (
    <div className="shrink-0">
      <button
        ref={btnRef}
        onClick={openMenu}
        className="p-1 mr-1 opacity-0 group-hover:opacity-100 transition-opacity"
        style={{ color: 'var(--ink-muted)' }}
      >
        <MoreHorizontal size={13} />
      </button>
      {menu}
    </div>
  )
}

function ConvRow({ conv }: { conv: { id: string; title: string | null; is_archived: boolean } }) {
  const { activeConvId, setActiveConvId, updateConversation, streamStates } = useConversations()
  const [renaming, setRenaming] = useState(false)
  const [newTitle, setNewTitle] = useState(conv.title || '')
  const isActive = conv.id === activeConvId
  const isStreaming = !!streamStates.get(conv.id)?.isStreaming

  async function commitRename() {
    const t = newTitle.trim() || conv.title || 'Nova conversa'
    await updateConversation(conv.id, { title: t })
    setRenaming(false)
  }

  if (renaming) {
    return (
      <div className="px-2">
        <input
          autoFocus
          value={newTitle}
          onChange={e => setNewTitle(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter') commitRename(); if (e.key === 'Escape') setRenaming(false) }}
          onBlur={commitRename}
          className="w-full px-3 py-2 text-xs rounded-lg border focus:outline-none"
          style={{ borderColor: 'var(--border)', background: 'white', color: 'var(--ink)' }}
        />
      </div>
    )
  }

  return (
    <div className="flex items-center group gap-1.5">
      {isStreaming && (
        <span
          className="w-2 h-2 rounded-full shrink-0 ml-1"
          style={{ background: 'var(--accent)', animation: 'stream-pulse 1.1s ease-in-out infinite' }}
          title="A gerar resposta"
        />
      )}
      <button
        onClick={() => setActiveConvId(conv.id)}
        className="flex-1 min-w-0 text-left px-3 py-2 text-xs rounded-lg truncate transition-colors"
        style={isActive
          ? { background: 'white', color: 'var(--ink)', fontWeight: 500, boxShadow: '0 1px 2px rgba(0,0,0,0.05)', border: '1px solid var(--border)' }
          : isStreaming
            ? { color: 'var(--accent)', fontWeight: 500, border: '1px solid transparent' }
            : { color: 'var(--ink-muted)', border: '1px solid transparent' }
        }
        onMouseEnter={e => { if (!isActive) { e.currentTarget.style.background = 'rgba(255,255,255,0.6)'; e.currentTarget.style.color = 'var(--ink)' }}}
        onMouseLeave={e => { if (!isActive) { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = isStreaming ? 'var(--accent)' : 'var(--ink-muted)' }}}
      >
        {conv.title || 'Nova conversa'}
      </button>
      <ConvMenu convId={conv.id} isArchived={conv.is_archived} onRename={() => { setNewTitle(conv.title || ''); setRenaming(true) }} />
    </div>
  )
}

/**
 * Compact sidebar row (B refined): dot-indicator + name + preview + HH:MM.
 * No avatar, ~50px tall. Used by both Rotinas and Chats sections.
 *
 * Bold name + accent-coloured dot when `unread=true`.
 */
function SidebarRow({
  id,
  name,
  preview,
  timeLabel,
  unread,
  active,
  streaming,
  onSelect,
  menu,
}: {
  id: string
  name: string
  preview: string | null
  timeLabel: string
  unread: boolean
  active: boolean
  streaming: boolean
  onSelect: (id: string) => void
  menu?: ReactNode
}) {
  return (
    <div
      className="group flex items-start gap-2.5 pr-1 cursor-pointer transition-colors"
      style={{
        paddingLeft: '10px',
        paddingTop: '9px',
        paddingBottom: '9px',
        borderLeft: active ? '2px solid var(--accent)' : '2px solid transparent',
        background: active ? 'rgba(255,255,255,0.65)' : 'transparent',
      }}
      onClick={() => onSelect(id)}
      onMouseEnter={e => { if (!active) e.currentTarget.style.background = 'rgba(0,0,0,0.02)' }}
      onMouseLeave={e => { if (!active) e.currentTarget.style.background = 'transparent' }}
    >
      {/* Leading indicator: 6px dot when unread; invisible placeholder otherwise
          so names align vertically across rows. Streaming shows a pulsing dot. */}
      <span
        className="w-1.5 h-1.5 rounded-full shrink-0"
        style={{
          marginTop: '7px',
          background: streaming || unread ? 'var(--accent)' : 'transparent',
          animation: streaming ? 'stream-pulse 1.1s ease-in-out infinite' : undefined,
        }}
      />
      <div className="flex-1 min-w-0">
        <div className="flex items-baseline gap-2">
          <span
            className="flex-1 min-w-0 truncate text-[13px]"
            style={{
              fontWeight: unread ? 600 : 500,
              color: 'var(--ink)',
            }}
          >
            {name}
          </span>
          <span
            className="shrink-0 text-[11px] tabular-nums"
            style={{
              color: unread ? 'var(--accent)' : 'var(--ink-muted)',
              fontWeight: unread ? 600 : 400,
            }}
          >
            {timeLabel}
          </span>
        </div>
        {preview && (
          <div
            className="text-[12px] truncate leading-snug mt-0.5"
            style={{ color: unread ? 'var(--ink-light)' : 'var(--ink-muted)' }}
          >
            {preview}
          </div>
        )}
      </div>
      {menu && (
        <div className="shrink-0 opacity-0 group-hover:opacity-100 transition-opacity">
          {menu}
        </div>
      )}
    </div>
  )
}

/**
 * Short time label à la WhatsApp: HH:MM if today, "ontem" if yesterday,
 * "DD mmm" otherwise. Handles the null case (no last message) with empty string.
 */
function shortTimeLabel(iso: string | null): string {
  if (!iso) return ''
  const d = new Date(iso)
  const now = new Date()
  const sameDay = d.toDateString() === now.toDateString()
  if (sameDay) {
    return `${d.getHours().toString().padStart(2, '0')}:${d.getMinutes().toString().padStart(2, '0')}`
  }
  const yesterday = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 1)
  if (d.toDateString() === yesterday.toDateString()) return 'ontem'
  const months = ['jan', 'fev', 'mar', 'abr', 'mai', 'jun', 'jul', 'ago', 'set', 'out', 'nov', 'dez']
  return `${d.getDate()} ${months[d.getMonth()]}`
}


function SectionHeader({ label, count, accent = false }: { label: string; count?: number; accent?: boolean }) {
  return (
    <div
      className="flex items-center justify-between px-3.5 pt-2.5 pb-1.5 text-[10px] font-semibold uppercase tracking-[0.08em]"
      style={{ color: accent ? 'var(--accent)' : 'var(--ink-muted)' }}
    >
      <span>{label}</span>
      {typeof count === 'number' && count > 0 && (
        <span className="text-[11px] font-normal normal-case tracking-normal" style={{ color: 'var(--ink-muted)' }}>
          {count}
        </span>
      )}
    </div>
  )
}

function ChatSidebar() {
  const {
    rotinaConversations,
    chatConversations,
    archivedConversations,
    activeConvId,
    setActiveConvId,
    markAgentConvSeen,
    streamStates,
    loadArchived,
  } = useConversations()
  const [showArchive, setShowArchive] = useState(false)
  const location = useLocation()

  useEffect(() => { setShowArchive(false) }, [location.pathname])

  async function toggleArchive() {
    if (!showArchive) await loadArchived()
    setShowArchive(v => !v)
  }

  // Clicking a rotina marks it seen (removes the unread dot) and selects it.
  function handleRotinaSelect(id: string) {
    markAgentConvSeen(id)
    setActiveConvId(id)
  }

  const newUnreadCount = rotinaConversations.filter(c => !c.seen_at).length

  return (
    <>
      {/* ─── Rotinas (scheduled recurring runs only) ─────────────── */}
      {rotinaConversations.length > 0 && (
        <div
          className="pb-2"
          style={{
            background: 'var(--cream-dark)',
            borderBottom: '1px solid var(--border)',
          }}
        >
          <SectionHeader
            label="Rotinas"
            accent
            count={newUnreadCount > 0 ? newUnreadCount : undefined}
          />
          <div>
            {rotinaConversations.map(conv => {
              const unread = !conv.seen_at
              const isActive = conv.id === activeConvId
              const isStreaming = !!streamStates.get(conv.id)?.isStreaming
              return (
                <SidebarRow
                  key={conv.id}
                  id={conv.id}
                  name={conv.title || 'Rotina'}
                  preview={conv.last_message_snippet}
                  timeLabel={shortTimeLabel(conv.last_message_at || conv.created_at)}
                  unread={unread}
                  active={isActive}
                  streaming={isStreaming}
                  onSelect={handleRotinaSelect}
                  menu={<ConvMenu convId={conv.id} isArchived={conv.is_archived} onRename={() => { /* rename não suportado para rotinas */ }} />}
                />
              )
            })}
          </div>
        </div>
      )}

      {/* ─── Chats (free chats + manual runs) ────────────────────── */}
      <SectionHeader label="Chats" count={chatConversations.length} />

      <div className="px-2 pb-2">
        <button
          onClick={() => setActiveConvId(null)}
          className="flex items-center gap-2 w-full px-3 py-2 text-xs rounded-lg transition-colors border border-transparent"
          style={{ color: 'var(--ink-muted)' }}
          onMouseEnter={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.6)'; e.currentTarget.style.color = 'var(--ink)'; e.currentTarget.style.borderColor = 'var(--border)' }}
          onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = 'var(--ink-muted)'; e.currentTarget.style.borderColor = 'transparent' }}
        >
          <Plus size={13} strokeWidth={1.5} />
          Nova conversa
        </button>
      </div>

      <div className="flex-1 overflow-y-auto flex flex-col">
        <div>
          {chatConversations.map(conv => {
            const isActive = conv.id === activeConvId
            const isStreaming = !!streamStates.get(conv.id)?.isStreaming
            return (
              <SidebarRow
                key={conv.id}
                id={conv.id}
                name={conv.title || 'Nova conversa'}
                preview={conv.last_message_snippet}
                timeLabel={shortTimeLabel(conv.last_message_at || conv.created_at)}
                unread={false}
                active={isActive}
                streaming={isStreaming}
                onSelect={setActiveConvId}
                menu={<ConvMenu convId={conv.id} isArchived={conv.is_archived} onRename={() => {}} />}
              />
            )
          })}
        </div>

        <div className="mt-auto pt-3 px-2 pb-2">
          <button
            onClick={toggleArchive}
            className="w-full flex items-center gap-2 px-3 py-1.5 text-xs transition-colors"
            style={{ color: 'var(--ink-muted)' }}
          >
            <Archive size={11} strokeWidth={1.5} />
            <span>Arquivo</span>
            <ChevronDown size={10} className={`ml-auto transition-transform ${showArchive ? 'rotate-180' : ''}`} />
          </button>

          {showArchive && (
            <div className="mt-1 space-y-0.5">
              {archivedConversations.length === 0 ? (
                <p className="px-3 py-2 text-xs" style={{ color: 'var(--ink-muted)' }}>Sem conversas arquivadas</p>
              ) : (
                archivedConversations.map(conv => (
                  <ConvRow key={conv.id} conv={conv} />
                ))
              )}
            </div>
          )}
        </div>
      </div>
    </>
  )
}

function ProfileMenu({ displayName, userInitials, email, signOut }: {
  displayName: string | null
  userInitials: string
  email: string | undefined
  signOut: () => void
}) {
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLDivElement>(null)

  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false)
    }
    if (open) document.addEventListener('mousedown', handleClick)
    return () => document.removeEventListener('mousedown', handleClick)
  }, [open])

  return (
    <div ref={ref} className="relative">
      {open && (
        <div
          className="absolute bottom-full left-0 right-0 mb-1 rounded-lg border overflow-hidden shadow-lg"
          style={{ background: 'var(--cream)', borderColor: 'var(--border)' }}
        >
          <div className="px-4 py-3" style={{ borderBottom: '1px solid var(--border)' }}>
            <p className="text-xs font-semibold truncate" style={{ color: 'var(--ink)' }}>{displayName || email?.split('@')[0] || ''}</p>
            {email && <p className="text-xs truncate mt-0.5" style={{ color: 'var(--ink-muted)' }}>{email}</p>}
          </div>
          <NavLink to="/rotinas" onClick={() => setOpen(false)}
            className="flex items-center gap-3 px-4 py-2.5 text-sm transition-colors"
            style={{ color: 'var(--ink-light)' }}
            onMouseEnter={e => e.currentTarget.style.background = 'var(--cream-dark)'}
            onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
            <Bot size={14} strokeWidth={1.5} style={{ color: 'var(--ink-muted)' }} />
            Rotinas
          </NavLink>
          <NavLink to="/connectors" onClick={() => setOpen(false)}
            className="flex items-center gap-3 px-4 py-2.5 text-sm transition-colors"
            style={{ color: 'var(--ink-light)' }}
            onMouseEnter={e => e.currentTarget.style.background = 'var(--cream-dark)'}
            onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
            <Plug size={14} strokeWidth={1.5} style={{ color: 'var(--ink-muted)' }} />
            Conectores
          </NavLink>
          <NavLink to="/settings" onClick={() => setOpen(false)}
            className="flex items-center gap-3 px-4 py-2.5 text-sm transition-colors"
            style={{ color: 'var(--ink-light)' }}
            onMouseEnter={e => e.currentTarget.style.background = 'var(--cream-dark)'}
            onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
            <Settings size={14} strokeWidth={1.5} style={{ color: 'var(--ink-muted)' }} />
            Definições
          </NavLink>
          <div style={{ borderTop: '1px solid var(--border)' }} />
          <button onClick={() => { setOpen(false); signOut() }}
            className="w-full flex items-center gap-3 px-4 py-2.5 text-sm transition-colors"
            style={{ color: 'var(--ink-light)' }}
            onMouseEnter={e => e.currentTarget.style.background = 'var(--cream-dark)'}
            onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
            <LogOut size={14} strokeWidth={1.5} style={{ color: 'var(--ink-muted)' }} />
            Sair
          </button>
        </div>
      )}
      <button
        onClick={() => setOpen(o => !o)}
        className="w-full flex items-center gap-2.5 px-3 py-2.5 rounded-lg transition-colors text-left"
        onMouseEnter={e => e.currentTarget.style.background = 'rgba(0,0,0,0.04)'}
        onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
      >
        <div className="w-7 h-7 rounded-full flex items-center justify-center text-xs font-semibold shrink-0"
          style={{ background: 'var(--accent)', color: 'white' }}>
          {userInitials}
        </div>
        <span className="flex-1 text-xs truncate min-w-0 font-medium" style={{ color: 'var(--ink-light)' }}>
          {displayName || email?.split('@')[0] || ''}
        </span>
        <ChevronDown size={11} className={`shrink-0 transition-transform ${open ? 'rotate-180' : ''}`} style={{ color: 'var(--ink-muted)' }} strokeWidth={1.5} />
      </button>
    </div>
  )
}

const SIDEBAR_WIDTH_KEY = 'seguru:sidebar-width'
const SIDEBAR_MIN = 160
const SIDEBAR_MAX = 420
const SIDEBAR_DEFAULT = 208  // matches the old w-52

function loadSidebarWidth(): number {
  try {
    const raw = localStorage.getItem(SIDEBAR_WIDTH_KEY)
    if (!raw) return SIDEBAR_DEFAULT
    const n = parseInt(raw, 10)
    if (Number.isNaN(n)) return SIDEBAR_DEFAULT
    return Math.min(SIDEBAR_MAX, Math.max(SIDEBAR_MIN, n))
  } catch { return SIDEBAR_DEFAULT }
}

export function AppShell({ children }: { children: ReactNode }) {
  const { user, signOut } = useAuth()
  const location = useLocation()
  const isChat = location.pathname.startsWith('/chat')
  // Inside a specific conversation on mobile, hide the bottom nav (WhatsApp
  // pattern — full screen for the thread). The list page `/chat` and all
  // other tabs keep the nav. Matches `/chat/<anything>` but not `/chat`.
  const isMobileChatThread =
    location.pathname.startsWith('/chat/') && location.pathname !== '/chat'

  const displayName = user?.user_metadata?.full_name || user?.user_metadata?.name || null
  const userInitials = initials(displayName, user?.email)

  const [sidebarWidth, setSidebarWidth] = useState<number>(loadSidebarWidth)
  const asideRef = useRef<HTMLElement>(null)
  const [isResizing, setIsResizing] = useState(false)

  // Chat-driven push subscribe (`enable_push_notifications` tool).
  // The chat tool emits a `seguru:enable-push` window event when the
  // user asks to enable notifications; we trigger the real subscribe
  // flow here so the popup runs on this device. Lives in AppShell so
  // it's mounted once across every authenticated route.
  const { subscribe: subscribePush } = usePushNotifications()
  useEffect(() => {
    const handler = () => {
      subscribePush().catch((err) => {
        console.warn('chat-driven push subscribe failed:', err)
      })
    }
    window.addEventListener('seguru:enable-push', handler)
    return () => window.removeEventListener('seguru:enable-push', handler)
  }, [subscribePush])

  const startResize = (e: React.MouseEvent) => {
    e.preventDefault()
    setIsResizing(true)
    const aside = asideRef.current
    if (!aside) return
    const startX = e.clientX
    const startWidth = aside.getBoundingClientRect().width
    const onMove = (ev: MouseEvent) => {
      const next = Math.min(SIDEBAR_MAX, Math.max(SIDEBAR_MIN, startWidth + (ev.clientX - startX)))
      setSidebarWidth(next)
    }
    const onUp = () => {
      document.removeEventListener('mousemove', onMove)
      document.removeEventListener('mouseup', onUp)
      setIsResizing(false)
      // Persist after the final value is committed via the last onMove.
      try {
        const final = asideRef.current?.getBoundingClientRect().width
        if (final) localStorage.setItem(SIDEBAR_WIDTH_KEY, String(Math.round(final)))
      } catch { /* ignore */ }
    }
    document.addEventListener('mousemove', onMove)
    document.addEventListener('mouseup', onUp)
  }

  // Double-click the handle to reset to default
  const resetSidebarWidth = () => {
    setSidebarWidth(SIDEBAR_DEFAULT)
    try { localStorage.setItem(SIDEBAR_WIDTH_KEY, String(SIDEBAR_DEFAULT)) } catch { /* ignore */ }
  }

  return (
    <div
      className="flex h-screen"
      style={{
        background: 'var(--cream)',
        fontFamily: 'var(--sans)',
        color: 'var(--ink)',
        cursor: isResizing ? 'col-resize' : undefined,
        userSelect: isResizing ? 'none' : undefined,
      }}
    >
      <aside
        ref={asideRef}
        className="hidden md:flex flex-col shrink-0 relative"
        style={{
          // D2 README: "sidebar funde com main, separadas por linha".
          // Antes era cream-dark; agora branco com border subtil.
          background: 'var(--surface)',
          borderRight: '1px solid var(--line)',
          width: `${sidebarWidth}px`,
        }}
      >
        <div className="px-5 pt-6 pb-4 flex items-center gap-2.5" style={{ borderBottom: '1px solid var(--line)' }}>
          <SeguruSymbol size={26} />
          <span style={{ fontFamily: 'var(--font-display)', fontSize: '20px', fontWeight: 400, letterSpacing: '-0.02em', color: 'var(--ink-1)' }}>
            seGuru
          </span>
        </div>

        {isChat ? (
          <ChatSidebar />
        ) : (
          <nav className="flex-1 px-2 py-3 space-y-0.5 overflow-y-auto">
            {mainNavItems.map(({ to, icon: Icon, label }) => (
              <NavLink
                key={to}
                to={to}
                className={({ isActive }) =>
                  `relative flex items-center gap-3 pl-4 pr-3 py-2 text-sm transition-colors ${
                    isActive ? 'font-medium' : ''
                  }`
                }
                style={({ isActive }) => isActive
                  // Regra D2 §1 — item activo: barra terracota 3px à
                  // esquerda + bold + fundo subtil. Hover em cinza puro
                  // (sem cor) para não disputar com o activo.
                  ? {
                      color: 'var(--ink-1)',
                      background: 'var(--surface-act)',
                      boxShadow: 'inset 3px 0 0 0 var(--accent)',
                    }
                  : { color: 'var(--ink-3)' }
                }
                onMouseEnter={e => {
                  if (!e.currentTarget.classList.contains('font-medium')) {
                    e.currentTarget.style.background = 'var(--surface-hov)'
                  }
                }}
                onMouseLeave={e => {
                  if (!e.currentTarget.classList.contains('font-medium')) {
                    e.currentTarget.style.background = 'transparent'
                  }
                }}
              >
                <Icon size={15} strokeWidth={1.5} />
                {label}
              </NavLink>
            ))}
          </nav>
        )}

        <div className="px-2 py-2" style={{ borderTop: '1px solid var(--line)' }}>
          <ProfileMenu displayName={displayName} userInitials={userInitials} email={user?.email} signOut={signOut} />
        </div>

        {/* Resize handle — drag to resize, double-click to reset */}
        <div
          onMouseDown={startResize}
          onDoubleClick={resetSidebarWidth}
          className="hidden md:block absolute top-0 right-0 h-full group"
          style={{
            width: '6px',
            marginRight: '-3px',          // half outside, half inside the border so it overlaps
            cursor: 'col-resize',
            zIndex: 10,
          }}
          title="Arrastar para redimensionar (duplo-clique para repor)"
        >
          <div
            className="h-full transition-colors"
            style={{
              width: '2px',
              marginLeft: '2px',
              background: isResizing ? 'var(--accent)' : 'transparent',
            }}
            onMouseEnter={e => { if (!isResizing) e.currentTarget.style.background = 'var(--border)' }}
            onMouseLeave={e => { if (!isResizing) e.currentTarget.style.background = 'transparent' }}
          />
        </div>
      </aside>

      <main
        className={
          'flex-1 flex flex-col overflow-hidden md:pb-0 ' +
          (isMobileChatThread ? 'pb-0' : 'pb-14')
        }
      >
        {children}
      </main>

      <nav
        className={
          (isMobileChatThread ? 'hidden' : 'md:hidden') +
          ' fixed bottom-0 left-0 right-0 flex justify-around py-2 z-50'
        }
        style={{ background: 'var(--cream)', borderTop: '1px solid var(--border)' }}>
        {mobileNavItems.map(({ to, icon: Icon, label }) => (
          <NavLink
            key={to}
            to={to}
            className="flex flex-col items-center gap-1 px-2 py-1 text-xs transition-colors"
            style={({ isActive }) => ({ color: isActive ? 'var(--ink)' : 'var(--ink-muted)' })}
          >
            <Icon size={19} strokeWidth={1.5} />
            {label}
          </NavLink>
        ))}
      </nav>
    </div>
  )
}
